import React, { useState } from 'react';
import { Card, CardContent, Box, IconButton, Collapse, Typography, Grid, Tooltip } from '@mui/material';
import { ExpandMore, Search, Info, Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TrolleyShelterLocationImageViewer from '@/components/TrolleyShelterLocationImageViewer';

interface NotificationRule {
    type: number;
    thresholds: any[];
}

interface Shelter {
    id: string;
    moduleId: string;
    capacity: number;
    disabled: boolean;
    deleteDate: string | null;
    description?: string;
    imagePath?: string;
    notificationRule: NotificationRule;
}

interface TrolleyShelterMonitoringCardProps {
    shelter: Shelter;
    fillLevel: number;
    getStatusColor: (shelter: Shelter) => string;
    getRequiredAction: (capacity: number, fillLevel: number) => string;
    getShelterProducts: (shelter: Shelter) => Array<{ name: string; requiredAction: string }>;
    types: {
        single: { id: number; text: string };
        perType: { id: number; text: string };
    };
}

const TrolleyShelterMonitoringCard: React.FC<TrolleyShelterMonitoringCardProps> = ({
    shelter,
    fillLevel,
    getStatusColor,
    getRequiredAction,
    getShelterProducts,
    types
}) => {
    const { t } = useTranslation();
    const [showDetails, setShowDetails] = useState(false);

    const hasContent =
        !!shelter.description || !!shelter.imagePath || shelter.notificationRule.type === types.perType.id;

    const statusColor = getStatusColor(shelter);
    const requiredAction = getRequiredAction(shelter.capacity, fillLevel);

    const getStatusColorValue = (color: string): string => {
        switch (color) {
            case 'success':
                return '#8AC926';
            case 'warning':
                return '#FFCA3A';
            case 'error':
                return '#FF595E';
            default:
                return '#45B4D9';
        }
    };

    const getStatusIcon = (color: string): React.ReactNode => {
        switch (color) {
            case 'success':
                return <Info sx={{ fontSize: '1.25rem' }} />;
            case 'warning':
                return <Warning sx={{ fontSize: '1.25rem' }} />;
            case 'error':
                return <Warning sx={{ fontSize: '1.25rem' }} />;
            default:
                return <Info sx={{ fontSize: '1.25rem' }} />;
        }
    };

    return (
        <Grid item xs={12} sm={6} md={4} lg={4}>
            <Card
                sx={{
                    height: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignSelf: 'flex-start'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        bgcolor: getStatusColorValue(statusColor),
                        color: 'white',
                        minHeight: 64
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Box sx={{ flexShrink: 0 }}>{getStatusIcon(statusColor)}</Box>
                        <Tooltip title={shelter.moduleId || ''} placement="top" arrow enterDelay={300} leaveDelay={200}>
                            <Typography
                                variant="h6"
                                component="div"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    minWidth: 0
                                }}
                            >
                                {shelter.moduleId}
                            </Typography>
                        </Tooltip>
                    </Box>
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{
                            fontWeight: 'bold',
                            flexShrink: 0,
                            ml: 1
                        }}
                    >
                        {requiredAction}
                    </Typography>
                </Box>

                {hasContent && (
                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Search sx={{ fontSize: '20px' }} />
                                <Typography variant="body2">{t('details')}</Typography>
                            </Box>
                            <IconButton
                                size="small"
                                onClick={() => setShowDetails(!showDetails)}
                                sx={{
                                    transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s'
                                }}
                            >
                                <ExpandMore />
                            </IconButton>
                        </Box>

                        <Collapse in={showDetails}>
                            <Box sx={{ mt: 2 }}>
                                {shelter.description && (
                                    <Box sx={{ mb: 1 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: 2
                                            }}
                                        >
                                            <Box sx={{ minWidth: '80px', flexShrink: 0 }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: 'text.secondary'
                                                    }}
                                                >
                                                    {t('description')}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Tooltip
                                                    title={shelter.description || ''}
                                                    placement="top"
                                                    arrow
                                                    enterDelay={300}
                                                    leaveDelay={200}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            display: 'block',
                                                            width: '100%'
                                                        }}
                                                    >
                                                        {shelter.description}
                                                    </Typography>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Box>
                                )}

                                {shelter.notificationRule.type === types.perType.id && (
                                    <Box sx={{ mb: 2 }}>
                                        {getShelterProducts(shelter).map((product, i) => (
                                            <Box
                                                key={i}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'flex-start',
                                                    gap: 2,
                                                    py: 0.5
                                                }}
                                            >
                                                <Box sx={{ minWidth: '20px', flexShrink: 0 }}>
                                                    <Typography variant="body2">🛒</Typography>
                                                </Box>

                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Tooltip
                                                        title={product.name || ''}
                                                        placement="top"
                                                        arrow
                                                        enterDelay={300}
                                                        leaveDelay={200}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                display: 'block',
                                                                width: '100%'
                                                            }}
                                                        >
                                                            {product.name || ''}
                                                        </Typography>
                                                    </Tooltip>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        minWidth: '60px',
                                                        flexShrink: 0,
                                                        textAlign: 'right'
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                        {product.requiredAction}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {shelter.imagePath && (
                                    <Box>
                                        <TrolleyShelterLocationImageViewer imagePath={shelter.imagePath} />
                                    </Box>
                                )}
                            </Box>
                        </Collapse>
                    </CardContent>
                )}
            </Card>
        </Grid>
    );
};

export default TrolleyShelterMonitoringCard;
